import { useEffect, useState, useCallback, useRef } from "react";
import { Wallet, Users, BarChart3, TrendingUp, RefreshCcw, ShieldCheck, AlertTriangle, Zap } from "lucide-react";
import { api } from "@/lib/api";
import { authHeader } from "@/lib/adminAuth";
import { toast } from "sonner";

const fmt = (n, dp = 2) =>
  n != null && Number.isFinite(+n)
    ? Number(n).toLocaleString(undefined, {
        minimumFractionDigits: dp,
        maximumFractionDigits: dp,
      })
    : "—";

// Pretty-prints time elapsed since `from` (ms epoch). "—" if from is null.
const fmtElapsed = (from) => {
  if (!from) return "—";
  const s = Math.max(0, Math.floor((Date.now() - from) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const Stat = ({ label, value, sub, icon: Icon, accent }) => (
  <div className="vts-card p-5">
    <div className="flex items-start justify-between mb-3">
      <div className="text-[10px] uppercase tracking-wider text-vts-silver/60 font-semibold">
        {label}
      </div>
      <Icon className={`h-4 w-4 ${accent || "text-vts-silver/60"}`} />
    </div>
    <div className={`font-mono text-2xl tabular-nums font-medium ${accent || "text-white"}`}>
      {value}
    </div>
    {sub && <div className="text-[11px] text-vts-silver/50 font-mono mt-1">{sub}</div>}
  </div>
);

const PartnerStats = ({ onUnauthorized } = {}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [official, setOfficial] = useState(null);
  const [officialLoading, setOfficialLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [tokenInput, setTokenInput] = useState("");
  const [savingToken, setSavingToken] = useState(false);

  const saveToken = async () => {
    const tok = tokenInput.trim();
    if (!tok) return;
    setSavingToken(true);
    try {
      await api.post("/partner/token", { token: tok }, { headers: authHeader() });
      toast.success("Token verified with Deriv — official markup is live again");
      setTokenInput("");
      fetchOfficial(); // eslint-disable-line no-use-before-define
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Deriv rejected that token");
    } finally {
      setSavingToken(false);
    }
  };

  // Live ticker — polls a 1-day window every 15s. Tracks the moment Deriv's
  // transaction count last increased so we can show "last paying trade Xs ago".
  const [today, setToday] = useState(null);
  const [lastTradeAt, setLastTradeAt] = useState(null);
  const [, setTick] = useState(0); // forces re-render every 1s for the timer
  const prevCountRef = useRef(null);

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get("/partner/earnings", { headers: authHeader() });
      setData(res.data);
    } catch (e) {
      if (e?.response?.status === 401 && onUnauthorized) onUnauthorized();
    } finally {
      setLoading(false);
    }
  }, [onUnauthorized]);

  const fetchOfficial = useCallback(async () => {
    setOfficialLoading(true);
    try {
      const res = await api.get(`/partner/markup-statistics?days=${days}`, {
        headers: authHeader(),
      });
      setOfficial(res.data);
    } catch (e) {
      if (e?.response?.status === 401 && onUnauthorized) onUnauthorized();
    } finally {
      setOfficialLoading(false);
    }
  }, [days, onUnauthorized]);

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 30000); // refresh every 30s
    return () => clearInterval(interval);
  }, [fetchStats]);

  useEffect(() => {
    fetchOfficial();
  }, [fetchOfficial]);

  // ── LIVE TICKER ── polls today's stats every 15s. When the official txn
  // count goes up, snapshot "now" so the timer can count up from there.
  useEffect(() => {
    let cancelled = false;
    const pull = async () => {
      try {
        const res = await api.get("/partner/markup-statistics?days=1", {
          headers: authHeader(),
        });
        if (cancelled) return;
        const next = res.data;
        setToday(next);
        const officialCount = next?.official?.total_transactions;
        const liveCount = (officialCount || 0) > 0 ? officialCount : (next?.estimated?.trade_count || 0);
        if (liveCount != null) {
          if (prevCountRef.current != null && liveCount > prevCountRef.current) {
            setLastTradeAt(Date.now());
          }
          prevCountRef.current = liveCount;
        }
      } catch {
        /* swallow — banner above will surface errors */
      }
    };
    pull();
    const id = setInterval(pull, 15000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  // 1-second tick to refresh "X seconds ago" without re-fetching the API
  useEffect(() => {
    const id = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <div data-testid="partner-stats" className="space-y-3">
      <div className="flex items-center justify-between mb-1">
        <div>
          <h3 className="font-heading font-semibold text-base text-white tracking-tight">
            Your partner earnings
          </h3>
          <div className="text-[11px] text-vts-silver/60 font-mono mt-0.5">
            Real-time markup + affiliate revenue · {data?.markup_pct ?? "—"}% markup
          </div>
        </div>
        <button
          data-testid="partner-stats-refresh"
          onClick={() => {
            fetchStats();
            fetchOfficial();
          }}
          className="text-[11px] text-vts-silver/60 hover:text-white p-2 rounded border border-vts-line bg-vts-surface hover:bg-vts-surfaceHover transition flex items-center gap-1.5"
        >
          <RefreshCcw className={`h-3 w-3 ${loading || officialLoading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* LIVE TICKER — markup flowing in real-time from Deriv */}
      {(() => {
        // Deriv's books post markup with up to a 24h delay — today's official
        // figures read $0 all day. Fall back to OUR real-time estimate so the
        // ticker actually moves while trades happen.
        const offMarkup = today?.official?.total_markup_usd ?? 0;
        const offCount = today?.official?.total_transactions ?? 0;
        const usingEstimate = offCount === 0;
        const todayMarkup = usingEstimate ? (today?.estimated?.total_markup ?? 0) : offMarkup;
        const todayCount = usingEstimate ? (today?.estimated?.trade_count ?? 0) : offCount;
        const isOfficialLive = today?.source === "official";
        const hasActivity = todayCount > 0;
        return (
          <div
            data-testid="partner-live-ticker"
            className={`vts-card p-4 border rounded-lg ${
              hasActivity
                ? "border-vts-profit/40 bg-gradient-to-r from-vts-profit/10 to-transparent"
                : "border-vts-line bg-vts-bg/40"
            }`}
          >
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="relative flex h-2.5 w-2.5">
                  <span
                    className={`absolute inline-flex h-full w-full rounded-full opacity-60 ${
                      hasActivity ? "bg-vts-profit animate-ping" : "bg-vts-silver/40"
                    }`}
                  ></span>
                  <span
                    className={`relative inline-flex rounded-full h-2.5 w-2.5 ${
                      hasActivity ? "bg-vts-profit" : "bg-vts-silver/50"
                    }`}
                  ></span>
                </span>
                <div className="text-[10px] uppercase tracking-wider text-vts-silver/70 font-mono">
                  Live · today
                </div>
              </div>
              <div className="flex items-baseline gap-1.5">
                <Zap
                  className={`h-4 w-4 ${
                    hasActivity ? "text-vts-profit" : "text-vts-silver/40"
                  }`}
                />
                <span
                  data-testid="ticker-today-markup"
                  className={`font-mono text-2xl tabular-nums font-medium ${
                    hasActivity ? "text-vts-profit" : "text-vts-silver/60"
                  }`}
                >
                  ${fmt(todayMarkup)}
                </span>
                <span className="text-[11px] text-vts-silver/60 font-mono">
                  · {todayCount} txn{todayCount === 1 ? "" : "s"}
                </span>
              </div>
              <div className="ml-auto text-right">
                <div className="text-[10px] uppercase tracking-wider text-vts-silver/60 font-mono">
                  Last paying trade
                </div>
                <div
                  data-testid="ticker-last-trade-ago"
                  className={`font-mono text-sm tabular-nums ${
                    lastTradeAt && Date.now() - lastTradeAt < 60000
                      ? "text-vts-profit"
                      : "text-vts-silver/80"
                  }`}
                >
                  {lastTradeAt ? fmtElapsed(lastTradeAt) : "no activity yet"}
                </div>
              </div>
            </div>
            {usingEstimate ? (
              <div className="text-[10px] text-vts-silver/50 font-mono mt-2" data-testid="ticker-lag-note">
                {hasActivity
                  ? "⚡ VTS live estimate — Deriv posts official markup with up to a 24h delay (today's trades hit their books tomorrow)"
                  : "No real-account trades booked yet today · Deriv's official books post with up to a 24h delay"}
              </div>
            ) : !isOfficialLive && (
              <div className="text-[10px] text-vts-silver/50 font-mono mt-2">
                Showing estimated figures · partner API not authenticated
              </div>
            )}
          </div>
        );
      })()}

      {/* OFFICIAL MARKUP from Deriv app_markup_statistics — pulled live */}
      <div
        data-testid="partner-official-block"
        className={`vts-card p-5 border ${
          official?.source === "official"
            ? "border-vts-profit/40 bg-vts-profit/5"
            : "border-vts-line"
        }`}
      >
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            {official?.source === "official" ? (
              <ShieldCheck className="h-4 w-4 text-vts-profit" />
            ) : (
              <AlertTriangle className="h-4 w-4 text-vts-silver/60" />
            )}
            <div>
              <div className="text-sm text-white font-medium">
                {official?.source === "official"
                  ? "Official markup (Deriv books)"
                  : "Official markup — fallback to estimate"}
              </div>
              <div className="text-[10px] text-vts-silver/60 font-mono mt-0.5">
                {official?.source === "official"
                  ? `app_markup_statistics · ${days === 1 ? "today" : `${days}-day window`}${
                      days <= 1 || (official?.official?.total_transactions ?? 0) === 0
                        ? " · Deriv posts markup with ~24h delay — today's trades appear tomorrow"
                        : ""
                    }`
                  : (() => {
                      const err = official?.official_error || "";
                      if (err === "rate_limited" || err.includes("429") || err.includes("1015"))
                        return "Deriv is rate-limiting stats requests — showing VTS estimate. Official numbers refresh automatically in a few minutes.";
                      return err || "Loading…";
                    })()}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 bg-vts-surface/60 border border-vts-line rounded-md p-0.5">
            {[1, 7, 30, 90].map((d) => (
              <button
                key={d}
                type="button"
                data-testid={`partner-days-${d}`}
                onClick={() => setDays(d)}
                className={`px-2.5 py-1 rounded text-[10px] font-mono uppercase tracking-wider transition ${
                  days === d
                    ? "bg-vts-blue text-white"
                    : "text-vts-silver/70 hover:text-white hover:bg-vts-surfaceHover"
                }`}
              >
                {d === 1 ? "today" : `${d}d`}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 rounded-md border border-vts-line bg-vts-bg/40">
            <div className="text-[10px] uppercase tracking-wider text-vts-silver/60 mb-1">
              Markup paid by Deriv
            </div>
            <div
              data-testid="partner-official-markup"
              className={`font-mono text-2xl tabular-nums font-medium ${
                official?.source === "official" ? "text-vts-profit" : "text-vts-silver/50"
              }`}
            >
              ${fmt(
                official?.source === "official"
                  ? official?.official?.total_markup_usd
                  : official?.estimated?.total_markup,
              )}
            </div>
            <div className="text-[10px] text-vts-silver/50 mt-1 font-mono">
              {official?.source === "official"
                ? `${official?.official?.total_transactions ?? 0} settled transactions`
                : "estimated (3% of payout · our DB)"}
            </div>
          </div>
          <div className="p-3 rounded-md border border-vts-line bg-vts-bg/40">
            <div className="text-[10px] uppercase tracking-wider text-vts-silver/60 mb-1">
              Estimated (our DB)
            </div>
            <div className="font-mono text-2xl tabular-nums font-medium text-vts-silver/80">
              ${fmt(official?.estimated?.total_markup)}
            </div>
            <div className="text-[10px] text-vts-silver/50 mt-1 font-mono">
              {official?.estimated?.trade_count ?? 0} VTS-routed trades
            </div>
          </div>
        </div>
        {official?.source === "official" && official?.official?.breakdown?.length > 0 && (
          <div className="mt-3 pt-3 border-t border-vts-line">
            <div className="text-[10px] uppercase tracking-wider text-vts-silver/60 mb-2 font-semibold">
              Per-app breakdown
            </div>
            <div className="space-y-1.5">
              {official.official.breakdown.slice(0, 5).map((b, i) => (
                <div
                  key={`${b.app_id || i}`}
                  className="flex items-center justify-between text-xs font-mono"
                >
                  <span className="text-vts-silver/70">app_id {b.app_id}</span>
                  <span className="text-vts-silver/60">
                    {b.transactions_count ?? 0} txns
                  </span>
                  <span className="text-vts-profit tabular-nums">
                    ${fmt(b.app_markup_usd)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
        {official?.source !== "official" && (
          <div className="mt-3 text-[10px] text-vts-silver/50 font-mono leading-relaxed">
            {official?.official_error?.includes("DERIV_PARTNER_TOKEN") ||
            official?.official_error?.includes("401") ||
            official?.official_error?.toLowerCase?.().includes("invalid") ||
            official?.official_error?.includes("log in") ? (
              <div className="space-y-2">
                <p>
                  The saved Deriv partner token was rejected (expired or revoked). Generate a fresh
                  API token at app.deriv.com/account/api-token (Read / Application-insights scope),
                  paste it below and it goes live instantly — no redeploy needed.
                </p>
                <div className="flex items-center gap-2">
                  <input
                    data-testid="partner-token-input"
                    type="password"
                    value={tokenInput}
                    onChange={(e) => setTokenInput(e.target.value)}
                    placeholder="paste new Deriv API token…"
                    className="flex-1 bg-vts-bg border border-vts-line rounded-md px-2.5 py-2 text-xs font-mono text-white placeholder:text-vts-silver/40 focus:outline-none focus:border-vts-blue"
                  />
                  <button
                    type="button"
                    data-testid="partner-token-save"
                    onClick={saveToken}
                    disabled={savingToken || !tokenInput.trim()}
                    className="px-3 py-2 rounded-md text-[10px] font-bold uppercase tracking-wider bg-vts-blue text-white disabled:opacity-40 hover:bg-vts-blue/80 transition-colors"
                  >
                    {savingToken ? "Verifying…" : "Save & verify"}
                  </button>
                </div>
                <p className="text-vts-silver/40">
                  The token is checked against Deriv before saving — a bad paste can't break your stats.
                </p>
              </div>
            ) : (
              "Showing estimated figures from our database. The official Deriv numbers will appear once the partner WebSocket call succeeds."
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat
          label="Markup commission"
          value={`$${fmt(data?.estimated_markup_commission ?? 0)}`}
          sub={`from ${data?.trade_count ?? 0} trades`}
          icon={Wallet}
          accent="text-vts-blue"
        />
        <Stat
          label="Trading volume"
          value={`$${fmt(data?.total_volume ?? 0)}`}
          sub="cumulative stakes"
          icon={BarChart3}
        />
        <Stat
          label="Referrals"
          value={fmt(data?.unique_referrals ?? 0, 0)}
          sub={`${data?.total_signups ?? 0} total logins`}
          icon={Users}
          accent="text-vts-profit"
        />
        <Stat
          label="Total signups"
          value={fmt(data?.total_signups ?? 0, 0)}
          sub="via your affiliate link"
          icon={TrendingUp}
        />
      </div>

      {data?.affiliate_token ? (
        <div className="vts-card p-4 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-vts-silver/60 mb-1 font-semibold">
              Active partner tracking
            </div>
            <div className="font-mono text-xs text-vts-blue truncate">
              Deriv Partners · t.deriv.link referral link live
            </div>
          </div>
          <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-vts-profit/15 text-vts-profit border border-vts-profit/30 font-mono shrink-0">
            ● Tracking
          </span>
        </div>
      ) : (
        <div className="vts-card p-4 border-vts-loss/30 bg-vts-loss/5">
          <div className="text-xs text-vts-loss font-medium mb-1">
            No affiliate token configured
          </div>
          <div className="text-[11px] text-vts-silver/60">
            Set DERIV_AFFILIATE_TOKEN in backend/.env to start earning partner
            commission on signups.
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerStats;
